// @ts-nocheck
import { useState } from "react";
import { Link } from "@corpuscomplete/web/components/link";
import type { PromiseWithServerResult } from "@rocicorp/zero";
import {
  MealPlanningMemberContextAgent,
  MealPlanningSessionPlannerAgent,
} from "@corpuscomplete/core/nutrition/meal-planning-agent";
import { MealPlanningSession } from "@corpuscomplete/core/nutrition/meal-planning-session";
import { AgentChat } from "@corpuscomplete/core-ui/ai/agent/chat";
import { AgentTools } from "@corpuscomplete/core-ui/ai/agent/tools";
import { MealPlanningAgentLifecycle } from "@corpuscomplete/core-ui/nutrition/meal-planning-agent/lifecycle";
import { MealPlanningMemberContext } from "@corpuscomplete/core-ui/nutrition/meal-planning-agent/member-context";
import { MealPlanningSessionPlanner } from "@corpuscomplete/core-ui/nutrition/meal-planning-agent/session-planner";
import { MealPlanningSessionDetail } from "@corpuscomplete/core-ui/nutrition/meal-planning-session/detail";
import { PlannedMealDetail } from "@corpuscomplete/core-ui/nutrition/planned-meal/detail";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { Env } from "@corpuscomplete/web/env";
import { StreamingIndicator, ToolError } from "@corpuscomplete/web/modules/common/agent-chat";
import { MessagePart } from "@corpuscomplete/web/modules/common/agent-chat/message-part";
import { ChatView } from "@corpuscomplete/web/modules/common/agent-chat/session-chat-view";
import {
  ToolAskQuestionsCompleted,
  ToolAskQuestionsPending,
} from "@corpuscomplete/web/modules/common/agent-chat/tools/ask-questions";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { NotFoundPage } from "@corpuscomplete/web/components/not-found-page";
import { PlannedMealEditorSections } from "@corpuscomplete/web/modules/nutrition/planned-meal/components/planned-meal-editor-sections";
import { ProgramWizardDialog } from "../../program/components/program-wizard";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@corpuscomplete/web-ui/components/card";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Progress } from "@corpuscomplete/web-ui/components/progress";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { WebSocket } from "partysocket";
import { Scope } from "@corpuscomplete/core/common/scope";

const createWebSocket = (path: string): AgentChat.WebSocketLike => {
  const apiUrl = new URL(Env.VITE_API_URL);
  const wsUrl = `${apiUrl.protocol === "https:" ? "wss:" : "ws:"}//${apiUrl.host}${path}`;
  const socket = new WebSocket(wsUrl);

  return {
    addEventListener: socket.addEventListener.bind(socket),
    send: socket.send.bind(socket),
    close: socket.close.bind(socket),
    get readyState() {
      return socket.readyState as 0 | 1 | 2 | 3;
    },
  };
};

export function MealPlanningSessionDetailPage({ id }: { id: string }) {
  const { userId } = useAuth();

  return (
    <MealPlanningSessionDetail.Provider sessionId={id} userId={userId}>
      <MealPlanningSessionWorkspace userId={userId} />
    </MealPlanningSessionDetail.Provider>
  );
}

function MealPlanningSessionWorkspace({ userId }: { userId: string }) {
  const session = MealPlanningSessionDetail.useSession();
  const details = MealPlanningSessionDetail.useLoadingState();
  const capabilities = MealPlanningSessionDetail.useCapabilities();
  const { selectedMeal, setSelectedMealId } = MealPlanningSessionDetail.useSelectedMeal();
  const memberOptions = MealPlanningSessionDetail.useMemberOptions();
  const mutations = MealPlanningSessionDetail.useMutations();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (details.isLoading) return null;
  if (!session) return <NotFoundPage />;
  const scope = Scope.fromId({ scopeId: session.scopeId, userId });

  const mutate = (result: PromiseWithServerResult | undefined) => {
    if (!result) return;
    setError(null);
    watchMutation(result, setError);
  };

  const memberLabels = new Map(memberOptions.map((member) => [member.userId, member.label]));

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Link
                to={
                  scope.kind === "personal"
                    ? "/personal/meal-planning-sessions"
                    : "/spaces/$spaceId/meal-planning-sessions"
                }
                params={scope.kind === "personal" ? {} : { spaceId: scope.spaceId }}
              >
                <Button variant="ghost" size="sm">
                  Back
                </Button>
              </Link>
              <Text className="truncate font-medium">
                {session.dateStart.toString()} to {session.dateEnd.toString()}
              </Text>
              <Badge variant="default">Planning</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen((open) => !open)}>
                Members
              </Button>
            </div>
          </div>
        </PageHeader>
        <PageContent className="px-4 py-4 sm:px-6">
          <div className="mx-auto grid w-full max-w-[96rem] gap-4 lg:grid-cols-[28rem_minmax(0,1fr)]">
            <div className="flex flex-col gap-3">
              {error && <Text className="text-destructive">{error}</Text>}
              {settingsOpen && (
                <MemberSettings
                  memberOptions={memberOptions}
                  mutate={mutate}
                  mutations={mutations}
                />
              )}
              <ScheduleConfigCard session={session} />
              <MealPlanningAgentPanel userId={userId} scope={scope} />
              <Card>
                <CardHeader>
                  <CardTitle>Meals</CardTitle>
                  <CardDescription>{session.plannedMeals.length} planned meals</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {session.plannedMeals.length === 0 && <Text variant="sm">No meals yet.</Text>}
                  {session.plannedMeals.map((meal) => (
                    <SessionMealCard
                      key={meal.id}
                      meal={meal}
                      memberLabels={memberLabels}
                      selected={selectedMeal?.id === meal.id}
                      onSelect={() => setSelectedMealId(meal.id)}
                    />
                  ))}
                </CardContent>
              </Card>
            </div>
            {selectedMeal ? (
              <PlannedMealDetail.Provider mealId={selectedMeal.id}>
                <SessionPlannedMealEditor
                  currentUserId={userId}
                  defaultScheduleDate={session.dateStart}
                  defaultScheduleTimeZone={session.timeZone}
                  memberLabels={memberLabels}
                  memberUserIds={session.group?.members.map((member) => member.userId) ?? []}
                  readOnly={!capabilities.canEditSessionMeals}
                />
              </PlannedMealDetail.Provider>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Text>Add a meal to start planning.</Text>
                </CardContent>
              </Card>
            )}
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function ScheduleConfigCard({ session }: { session: MealPlanningSession.Info }) {
  const assignments = session.scheduleConfig?.data.assignments ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Meal Schedule</CardTitle>
        <CardDescription>
          {assignments.length > 0
            ? assignments.map((assignment) => assignment.name).join(" · ")
            : "No schedule config found"}
        </CardDescription>
      </CardHeader>
      {assignments.length > 0 ? (
        <CardContent className="flex flex-col gap-2">
          {assignments.map((assignment) => (
            <div key={assignment.mealScheduleId} className="flex flex-wrap gap-1">
              {assignment.slots.map((slot) => (
                <Badge key={slot.mealScheduleSlotId} variant="secondary">
                  {slot.name}
                </Badge>
              ))}
            </div>
          ))}
        </CardContent>
      ) : null}
    </Card>
  );
}

function SessionMealCard({
  meal,
  memberLabels,
  onSelect,
  selected,
}: {
  meal: MealPlanningSession.Info["plannedMeals"][number];
  memberLabels: ReadonlyMap<string, string>;
  onSelect: () => void;
  selected: boolean;
}) {
  const violations = PlannedMealDetail.publishViolations(meal);
  const firstEvent = meal.events[0];
  const timeLabel = firstEvent ? PlannedMealDetail.eventSummary(firstEvent) : "No events";
  const itemCount = meal.plates.reduce((count, plate) => count + plate.items.length, 0);
  const descriptorCount = meal.plates.reduce(
    (count, plate) => count + plate.itemDescriptors.length,
    0,
  );

  return (
    <button
      className={cn(
        "rounded-xl border p-3 text-left transition-colors hover:bg-muted/60",
        selected && "border-primary bg-muted",
        violations.length > 0 && "border-destructive/40 bg-destructive/5",
      )}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Text
            className={cn(
              "truncate font-medium",
              !meal.name.trim() && "italic text-muted-foreground",
            )}
          >
            {meal.name.trim() || "Unnamed planned meal"}
          </Text>
          <Text variant="xs" className="mt-0.5 text-muted-foreground">
            {timeLabel}
          </Text>
        </div>
        <Badge variant={meal.status === "published" ? "default" : "outline"}>{meal.status}</Badge>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        <Badge variant="secondary">
          {itemCount} item{itemCount === 1 ? "" : "s"}
        </Badge>
        {descriptorCount > 0 ? (
          <Badge variant="destructive">
            {descriptorCount} descriptor{descriptorCount === 1 ? "" : "s"}
          </Badge>
        ) : null}
        {violations.length > 0 ? <Badge variant="destructive">Needs attention</Badge> : null}
      </div>
      {meal.plates.length > 0 ? (
        <div
          className="mt-2 flex flex-wrap gap-1"
          aria-label={`${meal.plates.length} meal members`}
        >
          {meal.plates.map((plate) => (
            <Badge key={plate.id} variant="outline" title={plate.userId}>
              {memberLabels.get(plate.userId) ?? shortUserId(plate.userId)}
            </Badge>
          ))}
        </div>
      ) : null}
    </button>
  );
}

function SessionPlannedMealEditor({
  currentUserId,
  defaultScheduleDate,
  defaultScheduleTimeZone,
  memberLabels,
  memberUserIds,
  readOnly,
}: {
  currentUserId: string;
  defaultScheduleDate: Temporal.PlainDate;
  defaultScheduleTimeZone: string;
  memberLabels: ReadonlyMap<string, string>;
  memberUserIds: readonly string[];
  readOnly: boolean;
}) {
  const meal = PlannedMealDetail.useMeal();
  const mutations = PlannedMealDetail.useMutations();
  const violations = PlannedMealDetail.usePublishViolations();
  const [error, setError] = useState<string | null>(null);
  if (!meal) return null;
  const mutate = (result: PromiseWithServerResult | undefined) => {
    if (!result) return;
    setError(null);
    watchMutation(result, setError);
  };
  return (
    <div className="flex flex-col gap-4">
      {error && <Text className="text-destructive">{error}</Text>}
      <PlannedMealEditorSections
        canEditContent={!readOnly && PlannedMealDetail.canEditContent(meal)}
        canEditSchedule={!readOnly && PlannedMealDetail.canEditSchedule(meal)}
        currentUserId={currentUserId}
        defaultScheduleDate={defaultScheduleDate}
        defaultScheduleTimeZone={defaultScheduleTimeZone}
        meal={meal}
        memberLabels={memberLabels}
        memberUserIds={memberUserIds}
        mutate={mutate}
        onRename={(name) => mutate(mutations.rename(name))}
        violations={violations}
      />
    </div>
  );
}

function MealPlanningAgentPanel({ userId, scope }: { userId: string; scope: Scope.Info }) {
  const session = MealPlanningSessionDetail.useSession();
  if (!session) return null;
  const sessionScope =
    scope.kind === "personal"
      ? ({ kind: "user", scopeId: session.scopeId, userId } as const)
      : ({ kind: "space", scopeId: session.scopeId, groupId: scope.spaceId } as const);

  return (
    <MealPlanningMemberContext.DataProvider
      userId={userId}
      mealPlanningSessionId={session.id}
      dateStart={session.dateStart}
      dateEnd={session.dateEnd}
      sessionScope={sessionScope}
    >
      <MealPlanningAgentPanelContent userId={userId} />
    </MealPlanningMemberContext.DataProvider>
  );
}

function MealPlanningAgentPanelContent({ userId }: { userId: string }) {
  const session = MealPlanningSessionDetail.useSession();
  const capabilities = MealPlanningSessionDetail.useCapabilities();
  const lifecycle = MealPlanningAgentLifecycle.useActiveSurface(
    session?.id ?? "00000000-0000-0000-0000-000000000000",
  );
  const plannerSession = MealPlanningSessionPlanner.useSession(
    session?.id ?? "00000000-0000-0000-0000-000000000000",
  );
  const memberContextSession = MealPlanningMemberContext.useMySession(
    session?.id ?? "00000000-0000-0000-0000-000000000000",
  );
  const memberContextResults = MealPlanningMemberContext.useResults(
    session?.id ?? "00000000-0000-0000-0000-000000000000",
  );
  const createMemberContextSession = MealPlanningMemberContext.useCreateSession();
  const createPlannerSession = MealPlanningSessionPlanner.useCreateSession();
  const [showCompletedMemberChat, setShowCompletedMemberChat] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const memberContextReadiness = MealPlanningMemberContext.useReadiness();
  const programPrerequisite = MealPlanningMemberContext.useProgramPrerequisite({
    userId,
    dateStart: session?.dateStart ?? Temporal.PlainDate.from("1970-01-01"),
    dateEnd: session?.dateEnd ?? Temporal.PlainDate.from("1970-01-01"),
    lifecycle,
  });
  const progressSummary = MealPlanningSessionPlanner.useMealWorkflowProgressSummary({
    plannerSession,
    plannedMeals: session?.plannedMeals ?? [],
  });

  if (!session) return null;

  const readiness = MealPlanningMemberContext.deriveReadinessSummary(session, memberContextResults);
  const startMemberAvailability = MealPlanningMemberContext.getStartAvailability({
    actorUserId: userId,
    session,
    plannerSessionExists: !!plannerSession,
    memberContextSessionExists: !!memberContextSession,
  });
  const editMemberAvailability = MealPlanningMemberContext.getEditAvailability({
    actorUserId: userId,
    linkedSessionUserId: memberContextSession?.userId,
    session,
    plannerSessionExists: !!plannerSession,
  });
  const startPlannerAvailability = MealPlanningSessionPlanner.getStartAvailability({
    canEditSessionMeals: capabilities.canEditSessionMeals,
    session,
    plannerSessionExists: !!plannerSession,
    memberContextsComplete: readiness.complete === readiness.total,
  });
  const showMemberChat =
    lifecycle.type === "member-context" &&
    lifecycle.sessionId &&
    (lifecycle.mode !== "complete-waiting" || showCompletedMemberChat);
  const mutate = (result: PromiseWithServerResult | undefined) => {
    if (!result) return;
    setError(null);
    watchMutation(result, setError);
  };

  if (lifecycle.type === "planner") {
    return (
      <Card className="h-[44rem]">
        <CardHeader>
          <CardTitle>Planning Assistant</CardTitle>
          <CardDescription>Draft meals with the shared planner.</CardDescription>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          {progressSummary ? <PlannerProgressSummary summary={progressSummary} /> : null}
          <MealPlanningAgentChat
            userId={userId}
            sessionId={lifecycle.sessionId}
            agentType={MealPlanningSessionPlannerAgent.definition.agentType}
            agentVersion={MealPlanningSessionPlannerAgent.definition.version}
            canEditSession={capabilities.canEditSessionMeals}
            deferredToolPresenter={MealPlanningSessionPlanner.deferredToolPresenter}
            renderMessage={(message) => <PlannerMessage message={message} session={session} />}
            renderPendingAction={(part, info) => (
              <PlannerPendingAction part={part} info={info} session={session} />
            )}
          />
        </CardContent>
      </Card>
    );
  }

  if (showMemberChat) {
    const memberChatSessionId = lifecycle.sessionId;
    if (!memberChatSessionId) return null;
    return (
      <Card className="h-[44rem]">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Your Preferences</CardTitle>
              <CardDescription>Tell the assistant what should shape this session.</CardDescription>
            </div>
            {lifecycle.mode === "complete-waiting" && (
              <Button variant="outline" size="sm" onClick={() => setShowCompletedMemberChat(false)}>
                Close
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <MealPlanningAgentChat
            userId={userId}
            sessionId={memberChatSessionId}
            agentType={MealPlanningMemberContextAgent.definition.agentType}
            agentVersion={MealPlanningMemberContextAgent.definition.version}
            canEditSession={editMemberAvailability.available}
            deferredToolPresenter={MealPlanningMemberContext.deferredToolPresenter}
            renderMessage={(message) => <MemberContextMessage message={message} />}
            renderPendingAction={(part, info) => (
              <MemberContextPendingAction part={part} info={info} />
            )}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Planning Assistant</CardTitle>
          <CardDescription>
            Collect member preferences before generating the session plan.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {error && <Text className="text-destructive">{error}</Text>}
          {programPrerequisite.shouldShow ? (
            <div className="rounded-lg border bg-muted/30 p-3">
              <Text className="font-medium">Set up nutrition targets</Text>
              <Text variant="xs" className="mt-1 text-muted-foreground">
                {MealPlanningMemberContext.getProgramPrerequisiteDescription(
                  programPrerequisite.readiness,
                )}
              </Text>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" onClick={programPrerequisite.openProgramWizard}>
                  Create Program
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={programPrerequisite.continueWithoutTargets}
                >
                  Continue without targets
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div>
                <Text className="font-medium">Your preferences</Text>
                <Text variant="xs" className="text-muted-foreground">
                  {lifecycle.mode === "not-started"
                    ? "Not started"
                    : "Saved and waiting for the planner"}
                </Text>
              </div>
              {lifecycle.mode === "not-started" ? (
                <Button
                  size="sm"
                  disabled={
                    !startMemberAvailability.available ||
                    programPrerequisite.shouldBlockStart ||
                    (memberContextReadiness.status !== "ready" &&
                      !programPrerequisite.shouldShow &&
                      memberContextReadiness.status !== "missing-nutrition-program")
                  }
                  onClick={() =>
                    mutate(
                      createMemberContextSession({ mealPlanningSessionId: session.id, userId }),
                    )
                  }
                >
                  Start
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!editMemberAvailability.available}
                  onClick={() => setShowCompletedMemberChat(true)}
                >
                  View/edit
                </Button>
              )}
              {!startMemberAvailability.available && lifecycle.mode === "not-started" ? (
                <Text variant="xs" className="text-muted-foreground">
                  {actionReasonLabel(startMemberAvailability.reason)}
                </Text>
              ) : null}
              {programPrerequisite.shouldBlockStart ||
              memberContextReadiness.status === "loading" ? (
                <Text variant="xs" className="text-muted-foreground">
                  Checking nutrition targets...
                </Text>
              ) : null}
              {!editMemberAvailability.available && lifecycle.mode !== "not-started" ? (
                <Text variant="xs" className="text-muted-foreground">
                  {actionReasonLabel(editMemberAvailability.reason)}
                </Text>
              ) : null}
            </div>
          )}
          <div className="rounded-lg border p-3">
            <Text className="font-medium">Group readiness</Text>
            <Text variant="xs" className="text-muted-foreground">
              {readiness.complete}/{readiness.total} complete · {readiness.inProgress} in progress ·{" "}
              {readiness.notStarted} not started
            </Text>
            <div className="mt-2 flex flex-col gap-1">
              {readiness.rows.map((row) => (
                <Text key={row.userId} variant="xs">
                  {row.label}: {row.status}
                </Text>
              ))}
            </div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Text className="font-medium">Shared planner</Text>
                <Text variant="xs" className="text-muted-foreground">
                  {readiness.incompleteMemberNames.length
                    ? `Waiting on ${readiness.incompleteMemberNames.join(", ")}`
                    : "Ready to draft meals"}
                </Text>
              </div>
              <Button
                size="sm"
                disabled={!startPlannerAvailability.available}
                onClick={() => mutate(createPlannerSession({ mealPlanningSessionId: session.id }))}
              >
                Start planner
              </Button>
            </div>
            {!startPlannerAvailability.available ? (
              <Text variant="xs" className="mt-2 text-muted-foreground">
                {actionReasonLabel(startPlannerAvailability.reason)}
              </Text>
            ) : null}
          </div>
          {session.memberContexts.length > 0 && (
            <div className="rounded-lg border p-3">
              <Text className="font-medium">Saved Member Context</Text>
              <div className="mt-2 flex flex-col gap-1">
                {session.memberContexts.map((context) => {
                  const member = session.group?.members.find(
                    (candidate) => candidate.userId === context.userId,
                  );
                  return (
                    <Text key={context.userId} variant="xs">
                      {member?.profile?.name ?? context.userId}:{" "}
                      {MealPlanningMemberContext.formatSummary(context.data)}
                    </Text>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <ProgramWizardDialog
        open={programPrerequisite.programWizardOpen}
        onClose={programPrerequisite.closeProgramWizard}
      />
    </>
  );
}

function PlannerProgressSummary({
  summary,
}: {
  summary: MealPlanningSessionPlanner.MealWorkflowProgressSummary;
}) {
  const statusText =
    summary.completedMeals === summary.totalMeals
      ? "Meal generation complete."
      : summary.hasActiveWork
        ? "Generating meals..."
        : summary.hasFailedWork
          ? "Meal generation finished with issues."
          : "Waiting on remaining meals.";
  return (
    <div className="px-4 pb-2">
      <div className="rounded-lg border p-3">
        <div className="flex items-center justify-between gap-3">
          <Text variant="xs" className="font-medium">
            {summary.completedMeals} / {summary.totalMeals} meals complete
            {summary.failedMeals ? ` · ${summary.failedMeals} need attention` : ""}
          </Text>
          <Text variant="xs" className="text-muted-foreground">
            {summary.progressPercent}%
          </Text>
        </div>
        <Progress value={summary.progressPercent} className="mt-2" />
        <Text variant="xs" className="mt-2 text-muted-foreground">
          {statusText}
        </Text>
      </div>
    </div>
  );
}

function actionReasonLabel(reason: string): string {
  return reason.split("_").join(" ");
}

function MealPlanningAgentChat({
  userId,
  sessionId,
  agentType,
  agentVersion,
  canEditSession,
  deferredToolPresenter,
  renderMessage,
  renderPendingAction,
}: {
  userId: string;
  sessionId: string;
  agentType: string;
  agentVersion: number;
  canEditSession: boolean;
  deferredToolPresenter: AgentTools.DeferredToolPresenter;
  renderMessage: (message: AgentChat.UIMessageWithStatus) => React.ReactNode;
  renderPendingAction: (
    part: AgentTools.DeferredTool,
    info: AgentTools.PendingActionRenderInfo & { isInteractive?: boolean },
  ) => React.ReactNode;
}) {
  return (
    <AgentChat.Provider
      userId={userId}
      agentType={agentType}
      agentVersion={agentVersion}
      sessionId={sessionId}
      canEditSession={canEditSession}
      createWebSocket={createWebSocket}
    >
      <ChatView
        className="min-h-0 flex-1"
        deferredToolPresenter={deferredToolPresenter}
        renderUIMessage={renderMessage}
        renderPendingAction={renderPendingAction}
      />
    </AgentChat.Provider>
  );
}

function MemberContextMessage({ message }: { message: AgentChat.UIMessageWithStatus }) {
  return (
    <AgentMessage
      message={message}
      renderTool={(part, key) => {
        if (
          MealPlanningMemberContext.isSaveMemberContextCompletedPart(part) &&
          MealPlanningMemberContext.isSaveMemberContextPart(part)
        ) {
          return (
            <ToolSummary
              key={key}
              title="Member context saved"
              body={MealPlanningMemberContext.formatSummary(part.output)}
            />
          );
        }
        if (MealPlanningMemberContext.isAskQuestionsCompletedPart(part)) {
          return <ToolAskQuestionsCompleted key={key} part={part} />;
        }
        return null;
      }}
    />
  );
}

function PlannerMessage({
  message,
  session,
}: {
  message: AgentChat.UIMessageWithStatus;
  session: MealPlanningSession.Info;
}) {
  return (
    <AgentMessage
      message={message}
      renderTool={(part, key) => {
        if (
          MealPlanningSessionPlanner.isProposePlanCompletedPart(part) &&
          MealPlanningSessionPlanner.isProposePlanPart(part)
        ) {
          return (
            <ToolSummary
              key={key}
              title="Plan approved"
              body={MealPlanningSessionPlanner.formatProposalSummary(part.output, session)}
            />
          );
        }
        return null;
      }}
    />
  );
}

function AgentMessage({
  message,
  renderTool,
}: {
  message: AgentChat.UIMessageWithStatus;
  renderTool: (part: AgentTools.DeferredTool, key: number) => React.ReactNode;
}) {
  return (
    <>
      {message.parts.map((part, i) => {
        if ("state" in part && part.state === "output-error")
          return <ToolError key={i} errorText={part.errorText} />;
        if (part.type === "text") {
          return (
            <MessagePart.Text
              key={i}
              role={message.role}
              part={part}
              isStreaming={message.status === "streaming"}
            />
          );
        }
        if (part.type === "step-start") return null;
        return renderTool(part, i);
      })}
    </>
  );
}

function MemberContextPendingAction({
  part,
  info,
}: {
  part: AgentTools.DeferredTool;
  info: AgentTools.PendingActionRenderInfo & { isInteractive?: boolean };
}) {
  if (MealPlanningMemberContext.isAskQuestionsPart(part) && part.input && "toolCallId" in part) {
    return (
      <ToolAskQuestionsPending
        messageId={info.messageId}
        part={part}
        isActive={info.isActive}
        isPersisted={info.isPersisted}
        isQueueInteractive={info.isInteractive ?? (info.isActive && info.isPersisted)}
        queuePosition={info.queuePosition}
        queueTotal={info.queueTotal}
        upNextLabel={info.upNextLabel}
      />
    );
  }
  if (MealPlanningMemberContext.isSaveMemberContextPart(part) && part.state === "input-streaming") {
    return <StreamingIndicator />;
  }
  if (
    !MealPlanningMemberContext.isSaveMemberContextPart(part) ||
    !isCompleteMemberContextResult(part.input) ||
    !("toolCallId" in part)
  )
    return null;
  return (
    <ReviewToolCard
      title="Save member context"
      body={MealPlanningMemberContext.formatSummary(part.input)}
      toolName={MealPlanningMemberContext.saveToolName}
      output={part.input}
      part={part}
      info={info}
    />
  );
}

function isCompleteMemberContextResult(
  input: MealPlanningMemberContextAgent.ToolResult | undefined,
): input is MealPlanningMemberContextAgent.ToolResult {
  return Array.isArray(input?.preferences);
}

function PlannerPendingAction({
  part,
  info,
  session,
}: {
  part: AgentTools.DeferredTool;
  info: AgentTools.PendingActionRenderInfo;
  session: MealPlanningSession.Info;
}) {
  if (MealPlanningSessionPlanner.isProposePlanPart(part) && part.state === "input-streaming") {
    return <StreamingIndicator />;
  }
  if (
    !MealPlanningSessionPlanner.isProposePlanPart(part) ||
    !isCompletePlannerProposal(part.input) ||
    !("toolCallId" in part)
  )
    return null;
  return (
    <ReviewToolCard
      title="Approve proposed plan"
      body={MealPlanningSessionPlanner.formatProposalSummary(part.input, session)}
      toolName={MealPlanningSessionPlanner.toolName}
      output={part.input}
      part={part}
      info={info}
    />
  );
}

function isCompletePlannerProposal(
  input: { meals?: MealPlanningSessionPlannerAgent.ProposedMeal[] } | undefined,
): input is { meals: MealPlanningSessionPlannerAgent.ProposedMeal[] } {
  return Array.isArray(input?.meals);
}

function ReviewToolCard<T>({
  title,
  body,
  toolName,
  output,
  part,
  info,
}: {
  title: string;
  body: string;
  toolName: string;
  output: T;
  part: AgentTools.DeferredTool & { toolCallId: string };
  info: AgentTools.PendingActionRenderInfo;
}) {
  const { submit, canSubmit } = AgentTools.useToolSubmit<T>({
    toolName,
    toolCallId: part.toolCallId,
    messageId: info.messageId,
  });
  const { cancel, canCancel } = AgentTools.useToolCancel({
    toolCallId: part.toolCallId,
    messageId: info.messageId,
  });
  const disabled = !info.isActive || !info.isPersisted;
  return (
    <div className="rounded-lg border bg-card p-3">
      <Text className="font-medium">{title}</Text>
      <div className="mt-1 max-h-40 overflow-y-auto rounded-md bg-muted/30 p-2">
        <Text variant="sm" className="text-muted-foreground">
          {body}
        </Text>
      </div>
      {info.upNextLabel && (
        <Text variant="xs" className="mt-2 text-muted-foreground">
          Next: {info.upNextLabel}
        </Text>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={disabled || !canCancel} onClick={cancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={disabled || !canSubmit} onClick={() => submit(output)}>
          Approve
        </Button>
      </div>
    </div>
  );
}

function ToolSummary({ title, body }: { title: string; body: string }) {
  return (
    <div className="my-2 rounded-lg border bg-muted/40 p-3">
      <Text className="font-medium">{title}</Text>
      <Text variant="sm" className="text-muted-foreground">
        {body}
      </Text>
    </div>
  );
}

function MemberSettings({
  memberOptions,
  mutate,
  mutations,
}: {
  memberOptions: readonly MealPlanningSessionDetail.MemberOption[];
  mutate: (result: PromiseWithServerResult | undefined) => void;
  mutations: MealPlanningSessionDetail.Mutations;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Session Members</CardTitle>
        <CardDescription>Removing assigned members may be blocked.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {memberOptions.map((member) => {
          return (
            <label key={member.userId} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={!member.canToggle}
                title={member.disabledReason ?? undefined}
                checked={member.isSelected}
                onChange={(event) =>
                  mutate(
                    event.target.checked
                      ? mutations.addMember(member.userId)
                      : mutations.removeMember(member.userId),
                  )
                }
              />
              <span>{member.label}</span>
            </label>
          );
        })}
      </CardContent>
    </Card>
  );
}

function watchMutation(
  result: PromiseWithServerResult,
  setError: (message: string | null) => void,
): void {
  void result.client
    .then((clientResult) => {
      if (clientResult.type === "error") setError(clientResult.error.message);
    })
    .catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : "Meal planning session update failed"),
    );
}

function shortUserId(userId: string): string {
  return userId.length > 8 ? `${userId.slice(0, 8)}...` : userId;
}
